import type { AgentWebSocket } from './websocket';
import type {
  Context,
  ContextHandler,
  IntentHandler,
  Listener,
} from '@finos/fdc3';

import { promiseIsFulfilled } from '../common/promise';
import { createContextListenerKey } from '@/common/interop/subscriptions';

export class Subscription<T> {
  public readonly id = crypto.randomUUID();
  constructor(public readonly handler: T) {}
}

export class SubscriptionManager {
  constructor(
    private contextListeners: Map<
      string,
      Subscription<ContextHandler>[]
    > = new Map(),
    private intentListeners: Map<
      string,
      Subscription<IntentHandler>[]
    > = new Map(),
  ) {}

  async callIntentListener(
    intent: string,
    context?: Context,
  ): Promise<Context | void> {
    const listeners = this.intentListeners.get(intent);
    if (!listeners) {
      return;
    }

    const allSettled = await Promise.allSettled(
      listeners.map((l) =>
        // Need the Promise.resolve since the handler can be synchronous
        Promise.resolve(
          (l.handler as IntentHandler).call(
            window,
            context || { type: 'fdc3.nothing' },
          ),
        ),
      ),
    );
    const results = allSettled
      .filter(promiseIsFulfilled)
      .map((p) => p.value)
      .filter(Boolean) as Context[];

    if (results.length === 0) {
      return;
    }

    if (results.length === 1) {
      return results[0];
    }

    return {
      type: 'connectifi.intentResults',
      results: results,
    };
  }

  callContextListeners = (
    currentChannel: string,
    context: Context,
    contextChannel?: string,
  ) => {
    let listeners: Subscription<ContextHandler>[] = [];
    // if context is for current channel, include any "channel-less" listeners
    if (currentChannel === contextChannel || contextChannel === undefined) {
      // listeners for all contexts
      const l = this.contextListeners.get('*');

      if (l) {
        listeners = listeners.concat(...l);
      }

      // listeners for context type
      const lc = this.contextListeners.get(context.type);

      if (lc) {
        listeners = listeners.concat(...lc);
      }
    }

    // channel listeners for all contexts
    const cl = this.contextListeners.get(`*|${contextChannel}`);

    if (cl) {
      listeners = listeners.concat(...cl);
    }

    // channel listeners for context type
    const clc = this.contextListeners.get(`${context.type}|${contextChannel}`);

    if (clc) {
      listeners = listeners.concat(...clc);
    }

    for (const listener of listeners) {
      listener.handler.call(window, context);
    }
  };

  dropContextListener(type: string, id: string): boolean {
    const listeners = this.contextListeners.get(type) || [];
    for (let i = 0; i < listeners.length; i++) {
      if (listeners[i].id === id) {
        listeners.splice(i, 1);
      }
    }

    return Boolean(!listeners.length);
  }

  createContextListener(
    ws: Pick<AgentWebSocket, 'action' | 'request'>,
    contextType: string,
    channel: string | undefined,
    handler: ContextHandler,
  ): Listener {
    const listenerItem = new Subscription(handler);
    const key = createContextListenerKey(contextType, channel);
    if (this.contextListeners.has(key)) {
      const listeners = this.contextListeners.get(key);
      listeners?.push(listenerItem);
    } else {
      this.contextListeners.set(key, [listenerItem]);
    }

    return {
      unsubscribe: () => {
        const noRemaining = this.dropContextListener(key, listenerItem.id);
        if (noRemaining) {
          ws.action('dropContextListener', {
            type: key,
          });
        }
      },
    };
  }

  dropIntentListener(intent: string, id: string): boolean {
    const listeners = this.intentListeners.get(intent) || [];
    for (let i = 0; i < listeners.length; i++) {
      if (listeners[i].id === id) {
        listeners.splice(i, 1);
      }
    }

    return Boolean(!listeners.length);
  }

  createIntentListener(
    ws: Pick<AgentWebSocket, 'action' | 'request'>,
    intent: string,
    handler: IntentHandler,
  ): Listener {
    const listenerItem = new Subscription(handler);
    if (this.intentListeners.has(intent)) {
      const listeners = this.intentListeners.get(intent);
      listeners?.push(listenerItem);
    } else {
      this.intentListeners.set(intent, [listenerItem]);
    }

    return {
      unsubscribe: () => {
        const noRemaining = this.dropIntentListener(intent, listenerItem.id);
        if (noRemaining) {
          ws.action('dropIntentListener', {
            intent,
          });
        }
      },
    };
  }
}
